import React, { useState } from "react";
import { Reveal, Overline } from "./Motion";
import { CONTACT, LOCATIONS } from "@/data/cafe";
import { submitContact } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";

const field = "rounded-sm border-[#1A1514]/15 bg-[#F5F2EB] focus-visible:ring-[#1C7C54] focus-visible:ring-1 focus-visible:ring-offset-0 font-body";
const labelCls = "font-accent text-xs uppercase tracking-[0.15em] text-[#1A1514]/60 mb-1.5 block";

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [mapIdx, setMapIdx] = useState(0);
  const submit = async (e) => {
    e.preventDefault();
    const f = e.target;
    setLoading(true);
    try {
      await submitContact({ name: f.name.value, email: f.email.value, phone: f.phone.value || null, message: f.message.value });
      toast.success("Message sent! We'll be in touch soon. ☕");
      f.reset();
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  const rows = [
    { icon: Phone, label: "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phone}` },
    { icon: MessageCircle, label: "WhatsApp", value: "Chat with us", href: CONTACT.socials.whatsapp },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: MapPin, label: "Visit", value: CONTACT.address, href: `https://maps.google.com/?q=${encodeURIComponent(CONTACT.address)}` },
  ];

  return (
    <section id="contact" className="bg-[#F5F2EB] py-24 md:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl">
          <Overline>Contact</Overline>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1A1514] mt-4 font-medium">
            Come say <span className="italic text-[#1C7C54]">hello</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-14">
          <div className="lg:col-span-5 space-y-3">
            {rows.map((r) => (
              <a key={r.label} href={r.href} target="_blank" rel="noreferrer" data-testid={`contact-${r.label.toLowerCase()}`}
                className="flex items-start gap-4 p-5 border border-[#1A1514]/12 rounded-sm hover:border-[#1C7C54] hover:bg-[#E5DFD3]/40 transition-colors group">
                <r.icon className="h-5 w-5 text-[#1C7C54] mt-0.5 shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="overline text-[#1A1514]/50">{r.label}</p>
                  <p className="font-body text-[#1A1514] mt-1 group-hover:text-[#1C7C54] transition-colors">{r.value}</p>
                </div>
              </a>
            ))}
            <div className="flex items-start gap-4 p-5 border border-[#1A1514]/12 rounded-sm">
              <Clock className="h-5 w-5 text-[#1C7C54] mt-0.5 shrink-0" strokeWidth={1.5} />
              <div>
                <p className="overline text-[#1A1514]/50">Opening Hours</p>
                {CONTACT.hours.map((h) => (
                  <p key={h.day} className="font-body text-[#1A1514] mt-1 flex justify-between gap-6">
                    <span>{h.day}</span><span className="text-[#1A1514]/60">{h.time}</span>
                  </p>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-rows-[auto_1fr] gap-5">
            <Reveal>
              <form onSubmit={submit} className="border border-[#1A1514]/12 rounded-sm p-7 space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className={labelCls}>Name</Label><Input name="name" required className={field} data-testid="contact-name" /></div>
                  <div><Label className={labelCls}>Phone</Label><Input name="phone" className={field} data-testid="contact-phone" /></div>
                </div>
                <div><Label className={labelCls}>Email</Label><Input name="email" type="email" required className={field} data-testid="contact-email" /></div>
                <div><Label className={labelCls}>Message</Label><Textarea name="message" rows={3} required className={field} data-testid="contact-message" /></div>
                <Button type="submit" disabled={loading} data-testid="contact-submit"
                  className="w-full h-11 rounded-sm bg-[#1C7C54] text-[#F5F2EB] hover:bg-[#14603F] font-accent uppercase tracking-[0.15em] text-xs transition-colors">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Message"}
                </Button>
              </form>
            </Reveal>
            <div className="overflow-hidden rounded-sm border border-[#1A1514]/12 min-h-[260px]" data-testid="contact-map-wrap">
              <div className="flex gap-1 p-1 bg-[#E5DFD3] border-b border-[#1A1514]/10" data-testid="contact-map-tabs">
                {LOCATIONS.map((l, i) => (
                  <button key={l.name} onClick={() => setMapIdx(i)} data-testid={`contact-map-tab-${i}`}
                    className={`flex-1 rounded-sm px-2 py-2 font-accent text-[10px] uppercase tracking-[0.12em] transition-colors ${
                      mapIdx === i ? "bg-[#1C7C54] text-[#F5F2EB]" : "text-[#1A1514]/60 hover:text-[#1A1514]"
                    }`}>
                    {l.name.replace("Teaavi Coffee — ", "")}
                  </button>
                ))}
              </div>
              <iframe title={LOCATIONS[mapIdx].name} data-testid="contact-map"
                src={`https://www.google.com/maps?q=${encodeURIComponent(LOCATIONS[mapIdx].address)}&output=embed`}
                className="w-full h-[240px] grayscale-[0.15]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
